import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent, SESSION_EVENT } from '../../utils/authSession';
import { shippingAccessScope } from '../../hooks/useShippingRequest';
import { tokenize, validateCard, tokenizationErrorMessage } from '../../utils/intuitCardToken';
import { useT } from '../../i18n/LanguageContext';
import { usd } from './cashMoney';
import {
  cashDepositMethod,
  readCashDeposit,
  sameCashDeposit,
  validCashPaymentOptions,
  validCashCardPaymentOptions,
  validCashCardFundingPolicy,
  validCashFundingUser,
} from './cashDepositContract';
import CashCardFields, { emptyCashCard } from './CashCardFields';

const unknown =
  'The payment outcome is unconfirmed. Do not send another payment. Refresh this request or contact MedEffects.';
const unwrap = (response) => response?.data?.data;
export default function CashDepositPayment({
  deposit,
  distributorId,
  disabled,
  onUpdated,
  onLockedChange,
}) {
  const t = useT();
  const [card, setCard] = useState(emptyCashCard);
  const [options, setOptions] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const mounted = useRef(false),
    pending = useRef(false),
    sent = useRef(Boolean(deposit.attempt));
  const binding = useRef({ session: captureSession(), scope: shippingAccessScope() });
  const callbacks = useRef({ onUpdated, onLockedChange });
  callbacks.current = { onUpdated, onLockedChange };
  const current = () =>
    mounted.current &&
    isSessionCurrent(binding.current.session) &&
    binding.current.scope === shippingAccessScope();
  const authorizedAccount = useCallback(() => {
    try {
      return validCashFundingUser(JSON.parse(localStorage.getItem('user')), distributorId);
    } catch {
      return false;
    }
  }, [distributorId]);
  const newCardRequest =
    cashDepositMethod(deposit) === 'card' &&
    deposit.authorizationVersion === 3 &&
    validCashCardFundingPolicy(deposit.fundingPolicy);
  const report = (value) => {
    pending.current = value;
    setBusy(value);
    callbacks.current.onLockedChange?.(value || sent.current);
  };
  useEffect(() => {
    mounted.current = true;
    const clear = () => {
      if (!current()) {
        setCard(emptyCashCard());
        setOptions(null);
      }
    };
    window.addEventListener(SESSION_EVENT, clear);
    window.addEventListener('storage', clear);
    return () => {
      mounted.current = false;
      window.removeEventListener(SESSION_EVENT, clear);
      window.removeEventListener('storage', clear);
    };
  }, []);
  useEffect(() => {
    let active = true;
    setOptions(null);
    setCard(emptyCashCard());
    if (deposit.attempt) sent.current = true;
    if (
      !newCardRequest ||
      !deposit.open ||
      deposit.status !== 'prepared' ||
      deposit.attempt ||
      sent.current ||
      !authorizedAccount()
    )
      return () => {
        active = false;
      };
    try {
      readCashDeposit(deposit);
    } catch {
      setError(unknown);
      return () => {
        active = false;
      };
    }
    distributorService
      .cashDepositPaymentOptions(deposit._id)
      .then((response) => {
        if (!active || !current() || !authorizedAccount()) return;
        const value = unwrap(response);
        if (!validCashPaymentOptions(value, deposit, distributorId))
          throw new Error('Invalid options');
        setOptions(value);
        setError('');
      })
      .catch(() => {
        if (active && current())
          setError('Card deposit options are unavailable. Reload them before authorizing.');
      });
    return () => {
      active = false;
    };
  }, [deposit, distributorId, newCardRequest, retry, authorizedAccount]);
  const apply = (response) => {
    const value = readCashDeposit(unwrap(response));
    if (!sameCashDeposit(value, deposit)) throw new Error('Unconfirmed deposit');
    if (value.attempt) sent.current = value.open;
    callbacks.current.onUpdated?.(value);
    return value;
  };
  const recover = async () => {
    if (!current() || pending.current || disabled) return;
    report(true);
    setCard(emptyCashCard());
    setError('');
    try {
      readCashDeposit(deposit);
      const response =
        (deposit.attempt || sent.current) && cashDepositMethod(deposit) !== 'wire'
          ? await distributorService.refreshCashDepositPayment(deposit._id)
          : await distributorService.cashDeposit(deposit._id);
      if (current()) apply(response);
    } catch {
      if (current()) setError(unknown);
    } finally {
      if (current()) report(false);
    }
  };
  const freshAuthorization = async () => {
    const [saved, configuration] = await Promise.all([
      distributorService.cashDeposit(deposit._id),
      distributorService.cashDepositPaymentOptions(deposit._id),
    ]);
    if (!current() || !authorizedAccount()) return null;
    const value = readCashDeposit(unwrap(saved)),
      allowed = unwrap(configuration);
    if (!sameCashDeposit(value, deposit)) throw new Error('Changed request identity');
    if (value.attempt || !value.open) {
      apply(saved);
      return null;
    }
    if (
      !validCashCardPaymentOptions(allowed, value, distributorId) ||
      allowed.tokenUrl !== options.tokenUrl
    )
      throw new Error('Changed payment readiness');
    return allowed;
  };
  const submit = async (event) => {
    event.preventDefault();
    if (
      !current() ||
      !authorizedAccount() ||
      pending.current ||
      sent.current ||
      disabled ||
      !validCashCardPaymentOptions(options, deposit, distributorId)
    )
      return;
    const problem = validateCard(card);
    if (problem) return setError(problem);
    const details = { ...card };
    setCard(emptyCashCard());
    report(true);
    setError('');
    let dispatched = false;
    try {
      const allowed = await freshAuthorization();
      if (!allowed) return;
      const token = await tokenize(allowed.tokenUrl, details);
      if (!current() || !authorizedAccount()) return;
      // Recheck after tokenization as well; a policy or access revocation never dispatches a charge.
      const confirmed = await freshAuthorization();
      if (!confirmed) return;
      sent.current = true;
      dispatched = true;
      callbacks.current.onLockedChange?.(true);
      const response = await distributorService.payCashDeposit(deposit._id, {
        token,
        authorized: true,
        authorizationVersion: 3,
        amountCents: deposit.amountCents,
        currency: 'USD',
        fundingPolicyVersion: 2,
      });
      if (current()) {
        const value = apply(response);
        if (!value.attempt) setError(unknown);
      }
    } catch (failure) {
      if (!current()) return;
      if (!dispatched)
        setError(
          tokenizationErrorMessage(failure) ||
            'Payment readiness changed or could not be verified. No new deposit payment was submitted. Reload the options and review again.'
        );
      else {
        try {
          const saved = await distributorService.cashDeposit(deposit._id);
          if (current()) {
            const value = apply(saved);
            if (!value.attempt) setError(unknown);
          }
        } catch {
          if (current()) setError(unknown);
        }
      }
    } finally {
      if (current()) {
        setCard(emptyCashCard());
        report(false);
      }
    }
  };
  const edit = (key, value) => {
    if (!current() || !authorizedAccount() || pending.current || sent.current || disabled) return;
    setCard((previous) => ({
      ...previous,
      [key]: value,
      ...(key === 'authorized' ? {} : { authorized: false }),
    }));
    setError('');
  };
  const locked = sent.current || Boolean(deposit.attempt);
  const ready = validCashCardPaymentOptions(options, deposit, distributorId) && authorizedAccount();
  return (
    <div className="me-cash" aria-label={t('Saved funding status')}>
      {error && <p role="alert">{t(error)}</p>}
      {busy && <p role="status">{t('Checking your cash funding request...')}</p>}
      {locked || !newCardRequest || !deposit.open ? (
        <>
          <p>
            {t(
              'This saved request is shown for history and status recovery only. It cannot submit another payment.'
            )}
          </p>
          <button type="button" disabled={busy || disabled} onClick={recover}>
            {t('Refresh funding status')}
          </button>
        </>
      ) : !ready ? (
        <>
          <p>{t('Card funding is unavailable. No deposit payment has been submitted.')}</p>
          <button
            type="button"
            disabled={busy || disabled}
            onClick={() => setRetry((value) => value + 1)}
          >
            {t('Reload card deposit options')}
          </button>
        </>
      ) : (
        <form onSubmit={submit} autoComplete="off">
          <dl className="me-cash-grid">
            <div>
              <dt>{t('Card charge')}</dt>
              <dd>{usd(deposit.amountCents)}</dd>
            </div>
            <div>
              <dt>{t('Tissue Bank credit after charge confirmation')}</dt>
              <dd>{usd(deposit.amountCents)}</dd>
            </div>
          </dl>
          <p>
            {t(
              'MedEffects covers the card processing fee. No processing fee is added to your card charge.'
            )}
          </p>
          <p>
            {t(
              'Card details are sent directly to QuickBooks. MedEffects does not store your card number or security code.'
            )}
          </p>
          <CashCardFields
            card={card}
            edit={edit}
            disabled={busy || disabled}
            amountCents={deposit.amountCents}
            authorizationVersion={3}
          />
          <div className="me-cash-actions">
            <button
              className="me-cash-primary"
              type="submit"
              disabled={busy || disabled || !card.authorized}
            >
              {t('Submit card deposit')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
